<template>
    <div v-if="show" class="modal-backdrop" @click.self="$emit('close')">
        <div class="modal-content">
            <div class="modal-header">
                <h3>Potential Tasks Found</h3>
                <button class="close-button" @click="$emit('close')">&times;</button>
            </div>

            <div class="modal-body">
                <div v-if="loading" class="loading-spinner">
                    Analyzing note for tasks...
                </div>

                <AITaskAnalyzer v-if="loading" :show="true" />
                
                <div v-else-if="tasks.length" class="task-list">
                    <p class="task-intro">I found these potential tasks in your note:</p>
                    
                    <div class="task-grid">
                        <div class="task-header">
                            <div class="col-check"></div>
                            <div class="col-task">Task</div>
                            <div class="col-assign">Relates To</div>
                            <div class="col-due">Due</div>
                            <div class="col-priority">Priority</div>
                        </div>
                        
                        <div v-for="task in tasks" :key="task.title" class="task-row" :class="{ selected: task.selected }">
                            <div class="col-check">
                                <input 
                                    type="checkbox" 
                                    v-model="task.selected" 
                                    :id="'task-' + task.title"
                                >
                            </div>
                            <div class="col-task">
                                <div class="task-content">
                                    <label :for="'task-' + task.title" class="task-title">{{ task.title }}</label>
                                    <p class="task-description" v-if="task.selected">{{ task.description }}</p>
                                </div>
                            </div>
                            <div class="col-assign">{{ formatRelatedUser(task.relatedTo) }}</div>
                            <div class="col-due">
                                <select v-model="task.dueInDays" :disabled="!task.selected" class="due-select">
                                    <option value="1">Tomorrow</option>
                                    <option value="2">2 days</option>
                                    <option value="3">3 days</option>
                                    <option value="7">1 week</option>
                                    <option value="14">2 weeks</option>
                                    <option value="21">3 weeks</option>
                                    <option value="30">1 month</option>
                                </select>
                            </div>
                            <div class="col-priority">
                                <select v-model="task.priority" :disabled="!task.selected" class="priority-select">
                                    <option value="high">High</option>
                                    <option value="medium">Medium</option>
                                    <option value="low">Low</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div class="modal-actions">
                        <button 
                            class="secondary" 
                            @click="$emit('close')"
                        >
                            Skip
                        </button>
                        <button 
                            class="primary" 
                            @click="createSelectedTasks"
                            :disabled="!selectedTasks.length"
                        >
                            Create {{ selectedTasksCount }} Tasks
                        </button>
                    </div>
                </div>

                <div v-else class="no-tasks">
                    <p>No tasks were found in this note.</p>
                    <button class="primary" @click="$emit('close')">Close</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent, ref, computed, watch } from 'vue';
import { taskService } from '../../services/taskService';
import AITaskAnalyzer from '../AITaskAnalyzer.vue';

export default defineComponent({
    name: 'TaskExtractorModal',
    
    components: {
        AITaskAnalyzer
    },
    
    emits: ['close', 'tasks-created', 'error'],
    
    props: {
        show: {
            type: Boolean,
            default: false
        },
        noteId: {
            type: Number,
            required: true
        },
        content: {
            type: [String, Object],
            required: true
        },
        mentionedUsers: {
            type: Array,
            required: true
        }
    },

    setup(props, { emit }) {
        const loading = ref(false);
        const error = ref(null);
        const tasks = ref([]);

        const formatRelatedUser = (userId) => {
            if (!userId) return 'No one';
            // Convert both to numbers for comparison
            const user = props.mentionedUsers.find(u => parseInt(u.id, 10) === userId);
            return user ? user.name : 'Unknown';
        };

        const selectedTasks = computed(() => {
            return tasks.value.filter(task => task.selected);
        });

        const selectedTasksCount = computed(() => {
            return selectedTasks.value.length;
        });

        const createSelectedTasks = async () => {
            try {
                loading.value = true;
                const selectedTasks = tasks.value.filter(task => task.selected);
                const tasksToCreate = selectedTasks.map(task => {
                    // Calculate the due date by adding dueInDays to current date
                    const dueDate = new Date();
                    dueDate.setDate(dueDate.getDate() + task.dueInDays);
                    
                    return {
                        title: task.title,
                        description: task.description,
                        priority: task.priority,
                        due_date: dueDate.toISOString(),  // Changed from dueDate to due_date
                        note_id: props.noteId,  // Changed from noteId to note_id
                        related_to_user_id: task.relatedTo  // Changed from relatedTo to related_to_user_id
                    };
                });

                const response = await taskService.createTasks(tasksToCreate);
                
                if (!Array.isArray(response)) {
                    console.error('Expected array response from createTasks, got:', response);
                    throw new Error('Invalid response format from server');
                }

                emit('tasks-created', response);
                emit('close');
            } catch (error) {
                console.error('Error creating tasks:', error);
                emit('error', error);
            } finally {
                loading.value = false;
            }
        };

        watch([() => props.content, () => props.mentionedUsers, () => props.noteId], 
            async (newValues) => {
                const [content, mentionedUsers, noteId] = newValues;

                if (!content || !mentionedUsers || !noteId) {
                    console.error('Missing required data for task extraction:', {
                        hasContent: !!content,
                        hasMentionedUsers: !!mentionedUsers,
                        hasNoteId: !!noteId
                    });
                    return;
                }

                try {
                    loading.value = true;
                    error.value = null;

                    const response = await taskService.extractTasks(content, mentionedUsers, noteId);

                    if (!response || !Array.isArray(response.tasks)) {
                        error.value = 'Failed to extract tasks: Invalid response format';
                        return;
                    }

                    tasks.value = response.tasks.map(task => ({
                        ...task,
                        selected: true,
                        dueInDays: 7,  // Default to 7 days
                        priority: 'medium'  // Default priority
                    }));
                } catch (err) {
                    console.error('Task extraction error:', err);
                    error.value = err.message || 'Failed to extract tasks';
                } finally {
                    loading.value = false;
                }
            },
            { immediate: true }
        );

        return {
            loading,
            tasks,
            error,
            selectedTasks,
            selectedTasksCount,
            createSelectedTasks,
            formatRelatedUser
        };
    }
});
</script>

<style scoped>
.modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-content {
    background: white;
    border-radius: 8px;
    width: 90%;
    max-width: 800px;
    max-height: 90vh;
    overflow-y: auto;
}

.modal-header {
    padding: 1rem;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.modal-body {
    padding: 1rem;
}

.task-grid {
    width: 100%;
    margin: 1rem 0;
}

.task-header {
    display: grid;
    grid-template-columns: 24px minmax(0, 1fr) 100px 100px 100px;
    gap: 0;
    padding: 0.75rem 0;
    border-bottom: 1px solid #e5e7eb;
    font-weight: 600;
    color: #374151;
}

.task-header > div,
.task-row > div {
    text-align: left;
}

.task-row {
    display: grid;
    grid-template-columns: 24px minmax(0, 1fr) 100px 100px 100px;
    gap: 0;
    padding: 0.75rem 0;
    border-bottom: 1px solid #e5e7eb;
    align-items: center;
}

.col-check {
    display: flex;
    align-items: center;
    justify-content: center;
}

.col-check input[type="checkbox"] {
    margin: 0;
}

.col-task {
    text-align: left;
    padding-right: 16px;
}

.col-assign {
    text-align: left;
}

.col-due {
    text-align: left;
}

.col-priority {
    text-align: left;
}

.task-content {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.task-title {
    font-weight: 500;
    color: #111827;
}

.task-description {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
}

.due-date {
    font-size: 0.75rem;
    color: #4b5563;
}

.due-select,
.priority-select {
    width: 100%;
    padding: 0.375rem;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    background: white;
    font-size: 0.875rem;
}

.task-row.selected {
    background-color: #f3f4f6;
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #e5e7eb;
}

button {
    padding: 0.5rem 1rem;
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
}

button.primary {
    background: #2563eb;
    color: white;
    border: none;
}

button.primary:disabled {
    background: #93c5fd;
    cursor: not-allowed;
}

button.secondary {
    background: white;
    color: #4b5563;
    border: 1px solid #d1d5db;
}

.loading-spinner {
    text-align: center;
    padding: 2rem;
    color: #6b7280;
}

.no-tasks {
    text-align: center;
    padding: 2rem;
    color: #6b7280;
}

.close-button {
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #6b7280;
    cursor: pointer;
}

.close-button:hover {
    color: #111827;
}
</style>
